import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { Dayjs } from 'dayjs';
import { MessageTemplateType } from 'hooks/useGetMessagesTemplates';
import { AudienceType } from 'types/create-campaign';

export type MessageEncodingType = 'GSM-7' | 'UCS-2';

export type CreateCampaignStore = {
  name: string;
  stores: string[];
  sendDate: Dayjs | null;
  audience: AudienceType | null;
  estimatedReachableUsers: number;
  message: string;
  messageTemplate: MessageTemplateType | null;
  sendTime: Dayjs | null;
  messageEncoding: MessageEncodingType;
  previewMessage: string;
  messagesQuantity: string;
  hasCredits: boolean;
  selectedChannel: 'sms' | 'push' | null;
  title: string;
  actions: {
    setName: (data: string) => void;
    setStores: (data: string[]) => void;
    setSendDate: (data: Dayjs | null) => void;
    setSendTime: (data: Dayjs | null) => void;
    setAudience: (data: AudienceType | null) => void;
    setMessage: (data: string) => void;
    setMessageTemplate: (data: MessageTemplateType | null) => void;
    setMessageEncoding: (data: MessageEncodingType) => void;
    setPreviewMessage: (data: string) => void;
    setMessagesQuantity: (data: string) => void;
    setHasCredits: (data: boolean) => void;
    reset: () => void;
    setSelectedChannel: (data: 'sms' | 'push' | null) => void;
    setCampaignTitle: (data: string) => void;
    clearComposeFields: () => void;
  };
};

const initialCreateCampaignStore: Omit<CreateCampaignStore, 'actions'> = {
  name: '',
  stores: [],
  sendDate: null,
  sendTime: null,
  audience: null,
  estimatedReachableUsers: 0,
  message: '',
  messageTemplate: null,
  previewMessage: '',
  messageEncoding: 'GSM-7',
  messagesQuantity: '0',
  hasCredits: false,
  selectedChannel: 'sms',
  title: '',
};

const CREATE_CAMPAIGN_STORE_NAME = 'create-campaign-store';

export const useCreateCampaignStore = create<CreateCampaignStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialCreateCampaignStore,
        actions: {
          setName: (data) => {
            set({ name: data });
          },
          setStores: (data) => {
            set({ stores: data });
          },
          setSendDate: (data) => {
            set({ sendDate: data });
          },
          setSendTime: (data) => {
            set({ sendTime: data });
          },
          setAudience: (data) => {
            set({ audience: data });
          },
          setMessage: (data) => {
            set({ message: data });
          },
          setMessageTemplate: (data) => {
            set({ messageTemplate: data });
          },
          setMessageEncoding: (data) => {
            set({ messageEncoding: data });
          },
          setPreviewMessage: (data) => {
            set({ previewMessage: data });
          },
          setMessagesQuantity: (data) => {
            set({ messagesQuantity: data });
          },
          setHasCredits: (data) => {
            set({ hasCredits: data });
          },
          setSelectedChannel: (data) => {
            set({ selectedChannel: data });
          },
          setCampaignTitle: (data) => {
            set({ title: data });
          },
          clearComposeFields: () => {
            set({
              message: '',
              messageTemplate: null,
              previewMessage: '',
              title: '',
              messagesQuantity: '0',
              messageEncoding: 'GSM-7',
            });
          },
          reset: () =>
            set({
              ...initialCreateCampaignStore,
            }),
        },
      }),
      {
        name: CREATE_CAMPAIGN_STORE_NAME,
        partialize: (state) => ({
          name: state.name,
          stores: state.stores,
          sendDate: state.sendDate,
          sendTime: state.sendTime,
          audience: state.audience,
          message: state.message,
          title: state.title,
          messageTemplate: state.messageTemplate,
          messageEncoding: state.messageEncoding,
          previewMessage: state.previewMessage,
          messagesQuantity: state.messagesQuantity,
          hasCredits: state.hasCredits,
          estimatedReachableUsers: state.estimatedReachableUsers,
          selectedChannel: state.selectedChannel,
        }),
      }
    ),
    {
      name: CREATE_CAMPAIGN_STORE_NAME,
    }
  )
);

export const useCreateCampaignStoreActions = () =>
  useCreateCampaignStore((state) => state.actions);
